import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useScoreApi } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import { runSheetManifestCheck } from "../local/sheetManifest";

// Card #3334 -- "no scoresheet left behind". Every CHECK_BASE_MS while online
// and idle, ask the server whether it still holds every sheet this tablet
// shipped for the current show (local/sheetManifest.js); each id it reports
// missing or stale is re-queued from the tablet's LIVE local copy as a
// resend op, which the server stores as a contender for a human to resolve.
//
// Same fire-and-forget posture as useStationSnapshotUpload.js: every failure
// is swallowed here, never thrown into a render, never a toast, never
// touches the auth-failure handler (kiosk doctrine: scribe-fe never
// interrupts scoring with an error).
//
// Back off on a bad network (AC7): a failed OR slow check doubles the wait,
// 1 -> 2, 4, 8 minutes, capped at 10; one clean, fast check resets to 1. A tick
// that is skipped because the tablet is offline or has outbox work in flight
// does NOT count as a failure: scoring traffic always goes first, the check
// just waits for the next tick.
export const CHECK_BASE_MS = 60 * 1000;
export const CHECK_MAX_MS = 10 * 60 * 1000;
// The first check after mount (a reload, a show switch) comes sooner than a
// full interval so a tablet that just woke up notices a lost sheet quickly.
export const CHECK_INITIAL_MS = 10 * 1000;
// A check slower than this is treated like a failure (a flaky venue link).
export const CHECK_SLOW_MS = 10 * 1000;

export function nextCheckDelayMs(consecutiveBadChecks) {
  return Math.min(CHECK_BASE_MS * 2 ** consecutiveBadChecks, CHECK_MAX_MS);
}

// "Outbox empty" for this purpose: nothing left that is competing for the
// network. Terminal ops (permanent, waiting on a human) and ops held for
// another owner are never retried, so they must not switch the safety net
// off for the whole show.
export function isOutboxIdle(snapshot) {
  const competing =
    (snapshot.pendingSync ?? 0) - (snapshot.terminalSync ?? 0) - (snapshot.heldSync ?? 0);
  return competing <= 0;
}

export function useSheetManifestCheck() {
  const scoreApi = useScoreApi();
  const auth = useAuth();
  const { selectedShow } = useScoreContext();
  const showId = selectedShow?.id ?? null;
  const isAuthenticated = Boolean(auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !showId) return undefined;

    let disposed = false;
    let timer = null;
    let latest = { online: true, pendingSync: 0, terminalSync: 0, heldSync: 0 };
    let badChecks = 0;

    const unsubscribe = syncEngine.subscribe((snapshot) => {
      latest = snapshot;
    });

    const schedule = (delayMs) => {
      if (disposed) return;
      timer = setTimeout(tick, delayMs);
    };

    async function tick() {
      if (disposed) return;
      if (latest.online === false || !isOutboxIdle(latest)) {
        schedule(nextCheckDelayMs(badChecks));
        return;
      }
      const startedAt = Date.now();
      try {
        await runSheetManifestCheck(scoreApi, showId);
        badChecks =
          Date.now() - startedAt > CHECK_SLOW_MS ? badChecks + 1 : 0;
      } catch {
        badChecks += 1;
      }
      schedule(nextCheckDelayMs(badChecks));
    }

    schedule(CHECK_INITIAL_MS);

    return () => {
      disposed = true;
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [scoreApi, showId, isAuthenticated]);
}

export default useSheetManifestCheck;
